import { useEffect, useMemo } from 'react';
import { Outlet, useLocation, Navigate } from 'react-router-dom';
import './Main.css';
import CookieConsentBanner from './components/cookie-consent/Banner';
import { LocationProvider } from './contexts/LocationContext';
import { OrgNavModeProvider } from './contexts/OrgNavModeContext';
import { DashboardLayoutProvider, useDashboardLayout } from './contexts/DashboardLayoutContext';
import { TopBarContentProvider } from './contexts/TopBarContentContext';
import { Toaster } from '../components/ui/toaster';
import NotificationPushManager from '../gedoba/notification/NotificationPushManager';
import NotificationInAppAlertManager from '../gedoba/notification/NotificationInAppAlertManager';
import TaxonomyLayout from '../admin/taxonomy/TaxonomyLayout';
import AdminLayout from '../admin/layout/AdminLayout';
import CommonLayout from '../components/layout/CommonLayout';
import MobileTabBar from '../components/layout/MobileTabBar';
import { dashboardLayoutConfig } from './components/Sidebar/dashboardConfig';
import { useAuth } from 'wasp/client/auth';
import ThemeProvider from './theme/ThemeProvider';
import { useQuery } from 'wasp/client/operations';
import { getSiteAccessStatus } from 'wasp/client/operations';
import {
  hasSiteAccess,
  grantSiteAccess,
  isSiteAccessGatePath,
  SITE_ACCESS_POST_GATE_PATH,
} from './site-access/siteAccess';

const TAXONOMY_PREFIXES = [
  '/admin/taxonomy',
  '/admin/taxonomy/location-names',
  '/admin/taxonomy/location-categories',
  '/admin/organization-types',
  '/admin/sections',
  '/admin/service-types',
  '/admin/service-definitions',
  '/admin/topic-types',
  '/admin/topics',
  '/admin/field-configs',
];

// Routes that should NOT use the persistent dashboard layout
const NO_DASHBOARD_PREFIXES = [
  '/login',
  '/signup',
  '/request-password-reset',
  '/password-reset',
  '/email-verification',
  '/pending-approval',
  '/oauth',
  '/about',
  '/privacy',
  '/terms',
  '/contact',
  '/roadmap',
];

function isNoDashboardRoute(pathname: string): boolean {
  // Exact match only — `startsWith('/')` would match every route.
  if (pathname === '/') return true;
  return NO_DASHBOARD_PREFIXES.some(prefix => pathname.startsWith(prefix));
}

// All routes that are publicly browsable without authentication.
// Add/edit/new operations are still protected.
const PUBLIC_EXACT = ['/reception', '/dashboard', '/search'];
const PUBLIC_CONTENT_PREFIXES = [
  '/notices',
  '/events',
  '/facilities',
  '/tenants',
  '/buy-sell',
  '/food-court',
  '/request-offer',
  '/transportation',
  '/housing',
  '/professionals',
  '/lost-found',
  '/services',
  '/information-desk',
];

// Known org section sub-paths — /:slug/:section is a public org page
const PUBLIC_ORG_SECTIONS = new Set(['about', 'notices', 'events', 'tenants', 'food-court', 'facilities', 'services', 'buy-sell', 'request-offer', 'lost-found', 'transportation', 'information-desk']);

function isPublicRoute(pathname: string): boolean {
  if (PUBLIC_EXACT.includes(pathname)) return true;
  if (PUBLIC_CONTENT_PREFIXES.some(prefix => {
    if (!pathname.startsWith(prefix)) return false;
    if (pathname.endsWith('/add') || pathname.endsWith('/new') || pathname.includes('/edit')) return false;
    return true;
  })) return true;
  // Org routes: /:slug/:section (e.g. /area-x-o/about) are public read-only views
  const segments = pathname.split('/').filter(Boolean);
  if (segments.length >= 2 && PUBLIC_ORG_SECTIONS.has(segments[1])) return true;
  return false;
}

function isCommunityRoute(pathname: string): boolean {
  return pathname.startsWith('/p/');
}



function AppShell() {
  const location = useLocation();
  const { data: user, isLoading } = useAuth();
  const { data: siteAccessStatus, isLoading: siteAccessLoading } = useQuery(getSiteAccessStatus);
  const {
    showViewSwitcher,
    activeView,
    onViewChange,
    forceSidebarCollapse,
  } = useDashboardLayout();

  const isTaxonomyPage = useMemo(() => {
    return TAXONOMY_PREFIXES.some(prefix => location.pathname.startsWith(prefix));
  }, [location.pathname]);

  const isAdminPage = useMemo(() => {
    return location.pathname.startsWith('/admin') && !isTaxonomyPage;
  }, [location.pathname, isTaxonomyPage]);

  const isNoDashboard = useMemo(() => isNoDashboardRoute(location.pathname), [location.pathname]);

  const isCommunity = false;

  useEffect(() => {
    if (location.hash) {
      const id = location.hash.replace('#', '');
      const element = document.getElementById(id);
      if (element) {
        element.scrollIntoView();
      }
    }
  }, [location]);

  const siteAccessEnabled = siteAccessStatus?.enabled ?? false;

  useEffect(() => {
    if (!siteAccessLoading && !siteAccessEnabled && !hasSiteAccess()) {
      grantSiteAccess();
    }
  }, [siteAccessLoading, siteAccessEnabled]);

  const siteAccessGranted =
    !siteAccessEnabled || hasSiteAccess();

  // Site password gate — when enabled in Global Settings or SITE_ACCESS_PASSWORD env, block until unlocked.
  if (!siteAccessLoading && siteAccessEnabled) {
    if (!siteAccessGranted && !isSiteAccessGatePath(location.pathname)) {
      return <Navigate to="/" replace />;
    }
    if (siteAccessGranted && isSiteAccessGatePath(location.pathname)) {
      return <Navigate to={SITE_ACCESS_POST_GATE_PATH} replace />;
    }
  }

  // Approval gate — block non-approved users from accessing any protected route.
  // Admins and platform owners always bypass regardless of accountStatus.
  const accountStatus = (user as any)?.accountStatus;
  const platformRole = (user as any)?.platformRole;
  const isPrivileged = platformRole === 'ADMINISTRATOR' || platformRole === 'PLATFORM_OWNER';
  const isOnPendingPage = location.pathname === '/pending-approval';

  // If the user is approved but stuck on the pending page (e.g. stale cache after
  // Google OAuth), redirect them to the dashboard so they can actually use the app.
  if (user && accountStatus === 'approved' && isOnPendingPage) {
    return <Navigate to="/dashboard" replace />;
  }

  if (
    user &&
    !isPrivileged &&
    accountStatus &&
    accountStatus !== 'approved' &&
    !isOnPendingPage &&
    !isNoDashboardRoute(location.pathname)
  ) {
    return <Navigate to="/pending-approval" replace />;
  }

  // While auth is resolving, render nothing on dashboard routes to avoid layout flash
  if (isLoading && !isNoDashboard) {
    return null;
  }

  // Unauthenticated users can browse public content; all other routes redirect to login
  if (!user && !isNoDashboard && !isPublicRoute(location.pathname)) {
    return <Navigate to="/login" replace />;
  }

  if (isTaxonomyPage) {
    return (
      <TaxonomyLayout>
        <Outlet />
      </TaxonomyLayout>
    );
  }

  if (isAdminPage) {
    return (
      <>
        <AdminLayout>
          <Outlet />
        </AdminLayout>
        {user && <MobileTabBar />}
      </>
    );
  }

  if (isNoDashboard) {
    return <Outlet />;
  }

  // Persistent dashboard layout — sidebar does NOT remount on navigation.
  // config is module-level constant so its reference never changes.
  return (
    <>
      <CommonLayout
        config={dashboardLayoutConfig}
        showViewSwitcher={showViewSwitcher}
        activeView={activeView}
        onViewChange={onViewChange}
        forceSidebarCollapse={forceSidebarCollapse}
        user={user}
      >
        <Outlet />
      </CommonLayout>
      {user && <MobileTabBar />}
    </>
  );
}

/**
 * use this component to wrap all child components
 * this is useful for templates, themes, and context
 */
export default function App() {
  return (
    <OrgNavModeProvider>
      <LocationProvider>
        <ThemeProvider>
          <DashboardLayoutProvider>
            <TopBarContentProvider>
              <div className='min-h-screen bg-background text-foreground'>
                <AppShell />
              </div>
              <NotificationPushManager />
              <NotificationInAppAlertManager />
              <CookieConsentBanner />
              <Toaster />
            </TopBarContentProvider>
          </DashboardLayoutProvider>
        </ThemeProvider>
      </LocationProvider>
    </OrgNavModeProvider>
  );
}



